import { useState, useEffect } from 'react';
import styles from './DatesList.module.css';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-day-light.svg';
import { ArrowDownIcon, TickCircleIcon } from 'evergreen-ui';
import { AnchorButton } from '..';
import FadeIn from 'react-fade-in';
import { useDispatch, useSelector } from 'react-redux';
import { getSubscriptions } from '../../actions/profile';
import { subscriptionIsValid } from '../../services/subscriptionsHelper';
import { useAppSelector } from 'store';
import { selectIsAuthenticated } from 'selectors';
import { renderPriceRange } from '../../services/renderPriceRange';
import { BookButton } from './components/BookButton';
import { Temporal } from 'temporal-polyfill';

export const DatesList = ({
    occurrences = [],
    parent = {},
    course = {},
    activeBooking = false,
    embedded,
    list_param,
    educator_slug,
}) => {
    const [limit, setLimit] = useState(12);
    const memberships = course?.memberships || [];
    const { subscriptions: studentSubscriptions } = useSelector((store) => store.profile);
    const dispatch = useDispatch();
    const isAuthenticated = useAppSelector(selectIsAuthenticated);

    /**
     *
     * @param {Temporal.ZonedDateTime} start
     * @param {Temporal.ZonedDateTime} end
     * @returns
     */
    const renderDateTimes = (start, end) => {
        if (start.toPlainDate().equals(end.toPlainDate())) {
            return (
                <span>
                    {`${start.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}, `}
                    {
                        <span className="">
                            {`${start.toLocaleString(undefined, { hour: 'numeric', minute: 'numeric' })} → ${end.toLocaleString(undefined, { hour: 'numeric', minute: 'numeric', timeZoneName: 'short' })}`}
                        </span>
                    }
                </span>
            );
        } else {
            return `${start.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'numeric', year: '2-digit', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' })} → ${end.toLocaleString(undefined, { day: 'numeric', month: 'numeric', year: '2-digit', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' })}`;
        }
    };

    const courseRequiredMemberships = memberships.reduce((mems, membership) => {
        const benefitsRelatedToCourse = membership.benefits.filter((benefit) =>
            benefit.course_refs.includes(course._id)
        );

        if (benefitsRelatedToCourse.some((benefit) => benefit.type === 'members-only')) {
            return mems.concat(membership);
        }
        return mems;
    }, []);
    // IDs of memberships that that the student has a valid subscription form
    const subMemIds = studentSubscriptions
        .filter(subscriptionIsValid)
        .map((sub) => sub.membership_ref);

    // List of membership IDs that the course needs
    const memIds = courseRequiredMemberships.map((m) => m._id);

    const studentHasRequiredMembership = memIds.reduce((has, mId) => {
        if (subMemIds.includes(mId)) return true;
        return has;
    }, false);

    useEffect(() => {
        if (isAuthenticated && !studentSubscriptions.length) {
            dispatch(getSubscriptions(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!occurrences || !occurrences.length) return null;

    return (
        <>
            {occurrences
                .slice(0, limit)
                .sort((a, b) => {
                    if (a && b) {
                        return Temporal.ZonedDateTime.compare(
                            Temporal.ZonedDateTime.from(a.times.start.zoned_datetime),
                            Temporal.ZonedDateTime.from(b.times.start.zoned_datetime)
                        );
                    }
                    return 1;
                })
                .map((child) => {
                    const isPassed = child.times.end.timestamp < Math.floor(Date.now() / 1000);
                    const isSoldOut =
                        !child.available_spaces ||
                        child.available_spaces < 1 ||
                        !parent.available_spaces ||
                        parent.available_spaces < 1;

                    return (
                        <FadeIn className={styles.date_item} key={child._id}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {isPassed ? (
                                    <TickCircleIcon
                                        size={16}
                                        color="var(--green)"
                                        style={{ marginRight: '10px' }}
                                    />
                                ) : (
                                    <CalendarIcon
                                        height="16px"
                                        color="var(--text-muted)"
                                        style={{ marginRight: '10px' }}
                                    />
                                )}
                                <span
                                    style={{ textDecoration: isPassed ? 'line-through' : 'none' }}
                                >
                                    {renderDateTimes(
                                        Temporal.ZonedDateTime.from(
                                            child.times.start.zoned_datetime
                                        ),
                                        Temporal.ZonedDateTime.from(child.times.end.zoned_datetime)
                                    )}
                                </span>
                            </div>

                            {!activeBooking && child.individually_bookable === true && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span>{renderPriceRange(child.offers)}</span>
                                    <BookButton
                                        child={child._id}
                                        course={course}
                                        embedded={embedded}
                                        listParam={list_param}
                                        isDisabled={
                                            isSoldOut + courseRequiredMemberships.length &&
                                            !studentHasRequiredMembership
                                        }
                                        educatorSlug={educator_slug}
                                    />
                                </div>
                            )}
                        </FadeIn>
                    );
                })}
            {occurrences.length < limit + 1 ? null : (
                <div key="view_more">
                    <span style={{ display: 'inline-block', margin: '10px' }}>
                        + {occurrences.length - limit} more
                    </span>
                    <AnchorButton onClick={() => setLimit(occurrences.length)}>
                        View all dates <ArrowDownIcon size={12} />
                    </AnchorButton>
                </div>
            )}
        </>
    );
};
